import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tab,
  Tabs,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AccessTime as TimeIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import taskService from '../../services/taskService';

const PRIORITY_CONFIG = {
  low: { color: '#94a3b8', label: 'Thấp' },
  medium: { color: '#3b82f6', label: 'Trung bình' },
  high: { color: '#f59e0b', label: 'Cao' },
  urgent: { color: '#ef4444', label: 'Khẩn cấp' },
};

const STATUS_CONFIG = {
  todo: { color: '#64748b', label: 'Chờ xử lý' },
  in_progress: { color: '#3b82f6', label: 'Đang làm' },
  review: { color: '#f59e0b', label: 'Chờ review' },
  done: { color: '#10b981', label: 'Hoàn thành' },
};

const CATEGORY_CONFIG = {
  marketing: { color: '#8b5cf6', label: 'Marketing' },
  social: { color: '#06b6d4', label: 'Social Media' },
  content: { color: '#10b981', label: 'Content' },
  production: { color: '#f59e0b', label: 'Sản xuất' },
  report: { color: '#6366f1', label: 'Báo cáo' },
};

const ACTION_LABELS = {
  created: 'đã tạo task',
  updated: 'đã cập nhật task',
  status_changed: 'đã đổi trạng thái',
  assigned: 'đã giao task',
  commented: 'đã bình luận',
  deadline_changed: 'đã đổi deadline',
  priority_changed: 'đã đổi độ ưu tiên',
};

const TaskDetailModal = ({ open, task, onClose, onTaskUpdated, users }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'marketing',
    priority: 'medium',
    status: 'todo',
    assignee: '',
    deadline: '',
  });

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        category: task.category || 'marketing',
        priority: task.priority || 'medium',
        status: task.status || 'todo',
        assignee: task.assignee?._id || '',
        deadline: task.deadline ? format(new Date(task.deadline), "yyyy-MM-dd'T'HH:mm") : '',
      });
    }
  }, [task]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const result = await taskService.updateTask(task._id, {
        ...formData,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
        assignee: formData.assignee || null,
      });
      if (result.success) {
        onTaskUpdated?.(result.data);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;

    setSubmittingComment(true);
    try {
      const result = await taskService.addComment(task._id, commentText.trim());
      if (result.success) {
        // Refresh task data
        const refreshed = await taskService.getTask(task._id);
        if (refreshed.success) {
          onTaskUpdated?.(refreshed.data);
        }
        setCommentText('');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  if (!task) return null;

  const statusConfig = STATUS_CONFIG[task.status];
  const priorityConfig = PRIORITY_CONFIG[task.priority];
  const categoryConfig = CATEGORY_CONFIG[task.category];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1,
        }}
      >
        <Box sx={{ flex: 1, pr: 2 }}>
          {isEditing ? (
            <TextField
              fullWidth
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              variant="outlined"
              size="small"
              placeholder="Tiêu đề task"
            />
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {task.title}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip
              label={categoryConfig?.label}
              size="small"
              sx={{
                bgcolor: categoryConfig?.color + '20',
                color: categoryConfig?.color,
                fontWeight: 600,
              }}
            />
            <Chip
              label={statusConfig?.label}
              size="small"
              sx={{
                bgcolor: statusConfig?.color + '20',
                color: statusConfig?.color,
                fontWeight: 600,
              }}
            />
            <Chip
              icon={<FlagIcon sx={{ fontSize: '14px !important' }} />}
              label={priorityConfig?.label}
              size="small"
              sx={{
                bgcolor: priorityConfig?.color + '20',
                color: priorityConfig?.color,
                fontWeight: 600,
                '& .MuiChip-icon': { color: priorityConfig?.color },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isEditing ? (
            <>
              <IconButton onClick={handleSave} disabled={loading} color="primary">
                {loading ? <CircularProgress size={20} /> : <SaveIcon />}
              </IconButton>
              <IconButton onClick={() => setIsEditing(false)}>
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <IconButton onClick={() => setIsEditing(true)}>
              <EditIcon />
            </IconButton>
          )}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ px: 2, borderBottom: '1px solid #f1f5f9' }}
      >
        <Tab label="Chi tiết" />
        <Tab label={`Bình luận (${task.comments?.length || 0})`} />
        <Tab label={`Lịch sử (${task.activityLog?.length || 0})`} />
      </Tabs>

      <DialogContent sx={{ p: 0 }}>
        {/* Details Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                {/* Description */}
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Mô tả
                </Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Nhập mô tả task..."
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: task.description ? 'text.primary' : 'text.secondary',
                      whiteSpace: 'pre-wrap',
                      p: 2,
                      bgcolor: '#f8fafc',
                      borderRadius: 2,
                      minHeight: 100,
                    }}
                  >
                    {task.description || 'Chưa có mô tả'}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                {/* Sidebar Info */}
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 2 }}>
                  {/* Status */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Trạng thái
                    </Typography>
                    {isEditing ? (
                      <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                        <Select
                          value={formData.status}
                          onChange={(e) => handleInputChange('status', e.target.value)}
                        >
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                            <MenuItem key={key} value={key}>
                              {config.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        label={statusConfig?.label}
                        size="small"
                        sx={{
                          mt: 0.5,
                          bgcolor: statusConfig?.color,
                          color: 'white',
                        }}
                      />
                    )}
                  </Box>

                  {/* Priority */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Độ ưu tiên
                    </Typography>
                    {isEditing ? (
                      <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                        <Select
                          value={formData.priority}
                          onChange={(e) => handleInputChange('priority', e.target.value)}
                        >
                          {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                            <MenuItem key={key} value={key}>
                              {config.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                        {priorityConfig?.label}
                      </Typography>
                    )}
                  </Box>

                  {/* Category */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Phân loại
                    </Typography>
                    {isEditing ? (
                      <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                        <Select
                          value={formData.category}
                          onChange={(e) => handleInputChange('category', e.target.value)}
                        >
                          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                            <MenuItem key={key} value={key}>
                              {config.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                        {categoryConfig?.label}
                      </Typography>
                    )}
                  </Box>

                  {/* Assignee */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Người phụ trách
                    </Typography>
                    {isEditing ? (
                      <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                        <Select
                          value={formData.assignee}
                          onChange={(e) => handleInputChange('assignee', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">Chưa giao</MenuItem>
                          {(users || []).map((user) => (
                            <MenuItem key={user._id} value={user._id}>
                              {user.fullName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        {task.assignee ? (
                          <>
                            <Avatar 
                              src={task.assignee.avatar}
                              sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: '#667eea' }}
                            >
                              {task.assignee.fullName?.[0]}
                            </Avatar>
                            <Typography variant="body2">{task.assignee.fullName}</Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Chưa giao
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Deadline */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Deadline
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        type="datetime-local"
                        size="small"
                        value={formData.deadline}
                        onChange={(e) => handleInputChange('deadline', e.target.value)}
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                        {task.deadline
                          ? format(new Date(task.deadline), 'dd/MM/yyyy HH:mm', { locale: vi })
                          : 'Chưa đặt'}
                      </Typography>
                    )}
                  </Box>

                  {/* Created Info */}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Tạo bởi: {task.createdBy?.fullName || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {task.createdAt && format(new Date(task.createdAt), 'dd/MM/yyyy HH:mm', { locale: vi })}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Comments Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            {/* Comment Input */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Viết bình luận..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmitComment()}
                size="small"
                multiline
                maxRows={3}
              />
              <IconButton
                onClick={handleSubmitComment}
                disabled={!commentText.trim() || submittingComment}
                color="primary"
                sx={{ bgcolor: '#667eea', color: 'white', '&:hover': { bgcolor: '#5a67d8' } }}
              >
                {submittingComment ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              </IconButton>
            </Box>

            {/* Comments List */}
            <List sx={{ p: 0 }}>
              {(task.comments || [])
                .slice()
                .reverse()
                .map((comment, index) => (
                  <ListItem key={index} alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar 
                        src={comment.userId?.avatar}
                        sx={{ bgcolor: '#667eea', width: 32, height: 32, fontSize: '0.8rem' }}
                      >
                        {comment.userId?.fullName?.[0] || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">
                            {comment.userId?.fullName || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(new Date(comment.createdAt), { locale: vi, addSuffix: true })}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                          {comment.content}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}

              {(!task.comments || task.comments.length === 0) && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  Chưa có bình luận
                </Typography>
              )}
            </List>
          </Box>
        )}

        {/* Activity Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <List sx={{ p: 0 }}>
              {(task.activityLog || [])
                .slice()
                .reverse()
                .map((activity, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 1 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#f1f5f9', color: '#64748b', width: 32, height: 32 }}>
                        <TimeIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          <strong>{activity.userId?.fullName || 'Someone'}</strong>{' '}
                          {ACTION_LABELS[activity.action] || activity.action}
                          {activity.field && (
                            <>
                              {' '}
                              <Chip
                                label={`${activity.oldValue || 'N/A'} → ${activity.newValue || 'N/A'}`}
                                size="small"
                                sx={{ ml: 0.5, height: 20, fontSize: '0.65rem' }}
                              />
                            </>
                          )}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(activity.createdAt), { locale: vi, addSuffix: true })}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}

              {(!task.activityLog || task.activityLog.length === 0) && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  Chưa có hoạt động
                </Typography>
              )}
            </List>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailModal;
